export const ALL_BRANCHES = "ALL_BRANCHES";
export const SELECTED_BRANCH = "SELECTED_BRANCH";
export const ANY = "ANY";
export const BEFORE_SELECTED_DATE = "BEFORE_SELECTED_DATE";
export const AFTER_SELECTED_DATE = "AFTER_SELECTED_DATE";
export const SELECTED_DATE_RANGE = "SELECTED_DATE_RANGE";

// ----- Dashboard date range presets -----
// Each preset resolves to a `{ dateFrom, dateTo }` window (YYYY-MM-DD)
// applied to the new dashboard endpoints. `custom` defers to the user's
// explicit picker selection.
export const DATE_RANGE_PRESETS = [
  { id: "7d", label: "Last 7 days", days: 7 },
  { id: "30d", label: "Last 30 days", days: 30 },
  { id: "90d", label: "Last 90 days", days: 90 },
  { id: "ytd", label: "Year to date" },
  { id: "custom", label: "Custom…" }
];

// Threshold below which a review is surfaced in the Recent low ratings list.
export const LOW_RATING_THRESHOLD = 3;

// ----- Chart palette (mono Deep Indigo) -----
// Charts that use a single hue draw from this gradient. Multi-bucket
// charts (rating distribution histogram) override per-bucket because
// 1-2 stars are red.
export const CHART_PALETTE = {
  ink: "#292359", // accent-500
  inkSoft: "#544F7A", // accent-400
  inkPale: "#7F7B9B", // accent-300
  inkFill: "#F4F4F7", // accent-50
  inkLine: "#E3E3E3",
  axis: "#AAAAAA"
};

// Per-grade colour for the rating distribution histogram.
// Anything <3 takes a red shade so poor reviews stand out at a glance.
export const RATING_BAR_COLOR = {
  5: "#292359",
  4: "#544F7A",
  3: "#7F7B9B",
  2: "#C84848",
  1: "#BA1A1A"
};

// Avatar palette for the technician ranking. Hash a name into one of
// these so each tech gets a stable colour without an extra API call.
export const TECH_AVATAR_PALETTE = [
  "#292359",
  "#544F7A",
  "#7F7B9B",
  "#3B8C35",
  "#A31717",
  "#1B173A"
];

// ----- Format helpers -----

export const formatCount = n => Number(n || 0).toLocaleString("en-US");

export const formatRating = n => {
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  return v.toFixed(1);
};

export const formatPct = n => {
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  return `${Math.round(v * 100)}%`;
};

export const formatSec = s => {
  const v = Number(s);
  if (!Number.isFinite(v) || v < 0) return "—";
  if (v >= 3600) {
    const h = Math.floor(v / 3600);
    const mm = Math.floor((v % 3600) / 60);
    return `${h}h ${mm}m`;
  }
  if (v >= 60) {
    const m = Math.floor(v / 60);
    const sec = Math.round(v % 60);
    return `${m}m ${String(sec).padStart(2, "0")}s`;
  }
  return `${Math.round(v)}s`;
};

export const formatDateShort = iso => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
};

export const formatDateTime = iso => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit"
  });
};

/**
 * Resolve a preset id to a concrete `{ dateFrom, dateTo }` ISO date range.
 * `custom` yields whatever the caller passed in via `customRange`.
 *
 * All calculations are done against the user's local calendar day
 * (today, year, etc.) but formatted as YYYY-MM-DD using local-date
 * parts — *not* `toISOString()`. Using ISO strings to format a
 * locally-constructed Date silently shifts the date by ±1 day in any
 * non-UTC timezone (e.g. `new Date(2026, 0, 1)` in Tokyo is
 * `2025-12-31` in UTC).
 */
const fmtLocalDate = d => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

export const resolveDateRange = (presetId, customRange = {}) => {
  const today = new Date();
  if (presetId === "custom") {
    return { dateFrom: customRange.dateFrom, dateTo: customRange.dateTo };
  }
  if (presetId === "ytd") {
    const start = new Date(today.getFullYear(), 0, 1);
    return { dateFrom: fmtLocalDate(start), dateTo: fmtLocalDate(today) };
  }
  const preset = DATE_RANGE_PRESETS.find(p => p.id === presetId);
  if (!preset || !preset.days) return {};
  const start = new Date(today);
  start.setDate(today.getDate() - preset.days + 1);
  return { dateFrom: fmtLocalDate(start), dateTo: fmtLocalDate(today) };
};

/**
 * Stable, deterministic colour pick from TECH_AVATAR_PALETTE based on
 * a name string.
 */
export const avatarBg = (name = "") => {
  let h = 0;
  for (let i = 0; i < name.length; i++) {
    h = (h * 31 + name.charCodeAt(i)) | 0;
  }
  return TECH_AVATAR_PALETTE[Math.abs(h) % TECH_AVATAR_PALETTE.length];
};
